import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import Icon from "@/components/Icon";
import api, { apiError } from "@/lib/api";

const lim = (v) => (v === null || v === undefined ? "Unlimited" : v);
const Card = ({ title, children, testid }) => <div data-testid={testid} className="rounded-2xl border border-border bg-card p-5"><h2 className="text-sm font-bold mb-3">{title}</h2>{children}</div>;

export default function PlanUsage() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/company/plan-usage").then(({ data }) => setD(data)).catch((e) => setErr(apiError(e.response?.data?.detail))); }, []);
  return (
    <AppLayout>
      <h1 className="text-2xl font-display font-extrabold mb-1">Plan & Usage</h1>
      <p className="text-sm text-muted-foreground mb-6">Only you (the owner) can see this page. Limits are set by your plan and by the platform.</p>
      {err && <div data-testid="plan-usage-error" className="rounded-xl border border-red-500/30 p-4 text-sm text-red-600">{err}</div>}
      {d && (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="plan-usage">
          <Card title="Subscription" testid="pu-subscription">
            <div className="text-2xl font-extrabold">{d.plan_label}</div>
            <div className="text-sm text-muted-foreground capitalize">Status: {d.subscription_status}{d.next_renewal ? ` · renews ${new Date(d.next_renewal).toLocaleDateString()}` : ""}</div>
            {d.override_active && <div className="text-xs mt-2 text-primary">Custom allowances apply to your workspace{d.override_expires_at ? ` until ${new Date(d.override_expires_at).toLocaleDateString()}` : ""}.</div>}
            <Link to="/app/billing" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary">Manage billing <Icon name="ArrowRight" className="h-3.5 w-3.5" /></Link>
          </Card>
          <Card title="Members" testid="pu-seats">
            <div className="text-sm space-y-1">
              <div>Allowed users (incl. you): <b data-testid="pu-seat-limit">{lim(d.seats.limit)}</b></div>
              <div>Active members: <b>{d.seats.active}</b> · Pending invitations: <b>{d.seats.pending}</b></div>
              <div>Available seats: <b data-testid="pu-seat-available">{lim(d.seats.available)}</b></div>
              {d.seats.over_limit && <div className="text-red-600 font-semibold">Over the member limit — invitations are paused. Nobody was removed.</div>}
              <div>QR codes: <b>{d.qr.used}</b> of <b>{lim(d.qr.limit)}</b></div>
            </div>
          </Card>
          <Card title="Features" testid="pu-features">
            <div className="grid sm:grid-cols-2 gap-1.5">
              {d.features.map((f) => (
                <div key={f.key} data-testid={`pu-feature-${f.key}`} className="flex items-center gap-2 text-sm">
                  <Icon name={f.enabled ? "CheckCircle2" : "XCircle"} className={`h-4 w-4 ${f.enabled ? "text-emerald-600" : "text-muted-foreground/60"}`} />
                  <span className={f.enabled ? "" : "text-muted-foreground"}>{f.label}{f.note ? ` (${f.note})` : f.source === "global_off" ? " (temporarily unavailable)" : ""}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card title="AI allowances" testid="pu-ai">
            {d.ai.map((a) => (
              <div key={a.feature} className="flex justify-between text-sm py-1 border-b border-border/60 last:border-0" data-testid={`pu-ai-${a.feature}`}>
                <span className={a.enabled ? "" : "text-muted-foreground"}>{a.label}</span>
                <span>{a.enabled ? <>{a.used} / {lim(a.allowance)} <span className="text-xs text-muted-foreground">{a.resets_at ? `· resets ${new Date(a.resets_at).toLocaleDateString()}` : "· no reset"}</span></> : "Not included"}</span>
              </div>
            ))}
          </Card>
        </div>
      )}
    </AppLayout>
  );
}
